import axios from 'axios';
import { postWithAiBilling } from '../../billing/billingApi';
import fnc from '../../mymod/commFunction';

const defaultServerUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:8002' : '';
const serverUrl = (process.env.REACT_APP_SERVER_URL || defaultServerUrl).replace(/\/$/, '');

function createApiError(message, requestId = '', cause = null, details = {}) {
  const error = new Error(message);
  error.name = 'EssayApiError';
  error.isEssayApiError = true;
  error.requestId = requestId;
  error.apiCode = details.apiCode || '';
  error.model = details.model || '';
  error.fallbackUsed = details.fallbackUsed === true;
  error.cause = cause;
  return error;
}

function getRequestErrorMessage(error) {
  if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return 'AI 분석 요청을 취소했습니다.';

  const status = error?.response?.status;
  if (status === 401) return '로그인이 만료되었습니다. 다시 로그인해 주세요.';
  const serverMessage = error?.response?.data?.error;
  if (serverMessage) return serverMessage;
  if (status === 413) return '첨부 파일 용량이 너무 큽니다. 더 작은 파일로 다시 시도해 주세요.';
  if (status === 415) return '지원하지 않는 파일 형식입니다. JPG, PNG, WEBP 또는 PDF 파일을 사용해 주세요.';
  if (status === 429) return 'AI 요청이 많습니다. 잠시 후 다시 시도해 주세요.';
  if (status === 502 || status === 504) return 'AI 분석이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.';
  if (error?.code === 'ECONNABORTED') return 'AI 분석 시간이 초과되었습니다. 다시 시도해 주세요.';
  if (!error?.response) return error?.message || 'AI 서버에 연결할 수 없습니다. 다시 확인해 주세요.';
  return 'AI 답안 진단을 완료하지 못했습니다. 다시 시도해 주세요.';
}

function getSessionToken() {
  try {
    return String(fnc.getCookie('appSessionToken') || '').trim();
  } catch (error) {
    return '';
  }
}

function getAuthenticatedRequestConfig(config = {}) {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw createApiError('로그인이 만료되었습니다. 다시 로그인해 주세요.');
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${sessionToken}`,
    },
  };
}

function createHistoryApiError(error, fallbackMessage) {
  if (error?.isEssayApiError) return error;
  const responseData = error?.response?.data || {};
  return createApiError(
    getRequestErrorMessage(error) || fallbackMessage,
    responseData.requestId || '',
    error,
    { apiCode: responseData.errorCode }
  );
}

export async function assessEssay({
  subject,
  taskType,
  assessmentType,
  gradeLevel,
  problemText,
  answerText,
  rubricText,
  problemAttachment,
  answerAttachment,
  signal,
  onUploadProgress,
}) {
  const formData = new FormData();
  formData.append('subject', subject);
  formData.append('taskType', taskType);
  formData.append('assessmentType', assessmentType || 'essay');
  formData.append('gradeLevel', gradeLevel);
  formData.append('problemText', problemText || '');
  if (taskType === 'diagnose') {
    formData.append('answerText', answerText || '');
    formData.append('rubricText', rubricText || '');
  }
  if (problemAttachment?.file) {
    formData.append('problemFile', problemAttachment.file, problemAttachment.name);
  }
  if (taskType === 'diagnose' && answerAttachment?.file) {
    formData.append('answerFile', answerAttachment.file, answerAttachment.name);
  }

  try {
    const response = await postWithAiBilling('essay_assess', `${serverUrl}/aiEssay/assess`, formData, getAuthenticatedRequestConfig({
      signal,
      timeout: 150000,
      onUploadProgress,
    }));

    if (!response.data?.success || !response.data?.result) {
      throw createApiError(
        response.data?.error || 'AI 진단 결과가 없습니다.',
        response.data?.requestId,
        null,
        {
          apiCode: response.data?.errorCode,
          model: response.data?.model,
          fallbackUsed: response.data?.fallbackUsed,
        }
      );
    }

    return {
      result: response.data.result,
      requestId: response.data.requestId || '',
      model: response.data.model || '',
      provider: response.data.provider || '',
      fallbackUsed: response.data.fallbackUsed === true,
      historySaved: response.data.historySaved === true,
      historyId: response.data.historyId || '',
      historyError: response.data.historyError || '',
    };
  } catch (error) {
    if (error?.isEssayApiError) throw error;
    const responseData = error?.response?.data || {};
    throw createApiError(
      getRequestErrorMessage(error),
      responseData.requestId || '',
      error,
      {
        apiCode: responseData.errorCode,
        model: responseData.model,
        fallbackUsed: responseData.fallbackUsed,
      }
    );
  }
}

export async function extractOmrAnswerSheet({ answerSheetAttachment, questions, signal, onUploadProgress }) {
  if (!answerSheetAttachment?.file) {
    throw createApiError('판독할 전체 답안지 이미지를 첨부해 주세요.');
  }

  const formData = new FormData();
  formData.append('answerSheet', answerSheetAttachment.file, answerSheetAttachment.name);
  formData.append('questions', JSON.stringify(Array.isArray(questions) ? questions : []));

  try {
    const response = await postWithAiBilling('omr_extract', `${serverUrl}/aiEssay/omr-answer-sheet/extract`, formData, getAuthenticatedRequestConfig({
      signal,
      timeout: 150000,
      onUploadProgress,
    }));

    if (!response.data?.success || !Array.isArray(response.data?.answers)) {
      throw createApiError(
        response.data?.error || '답안지 판독 결과가 없습니다.',
        response.data?.requestId,
        null,
        {
          apiCode: response.data?.errorCode,
          model: response.data?.model,
        }
      );
    }

    return {
      answers: response.data.answers,
      warnings: Array.isArray(response.data.warnings) ? response.data.warnings : [],
      requestId: response.data.requestId || '',
      model: response.data.model || '',
      provider: response.data.provider || '',
    };
  } catch (error) {
    if (error?.isEssayApiError) throw error;
    const responseData = error?.response?.data || {};
    throw createApiError(
      getRequestErrorMessage(error),
      responseData.requestId || '',
      error,
      {
        apiCode: responseData.errorCode,
        model: responseData.model,
      }
    );
  }
}

export async function gradeOmrAnswerSheets({
  answerSheetAttachment,
  modelAnswerSheetKey,
  acaCode,
  parentCode,
  questions,
  signal,
  onUploadProgress,
}) {
  if (!answerSheetAttachment?.file) {
    throw createApiError('학생 전체 답안지 이미지를 첨부해 주세요.');
  }
  if (!modelAnswerSheetKey) {
    throw createApiError('선생님이 등록한 전체 모범답안 이미지가 없습니다.');
  }

  const formData = new FormData();
  formData.append('answerSheet', answerSheetAttachment.file, answerSheetAttachment.name);
  formData.append('modelAnswerSheetKey', modelAnswerSheetKey);
  formData.append('acaCode', acaCode || '');
  formData.append('parentCode', parentCode || '');
  formData.append('questions', JSON.stringify(Array.isArray(questions) ? questions : []));

  try {
    const response = await postWithAiBilling('omr_grade', `${serverUrl}/aiEssay/omr-answer-sheet/grade`, formData, getAuthenticatedRequestConfig({
      signal,
      timeout: 180000,
      onUploadProgress,
    }));
    if (!response.data?.success || !Array.isArray(response.data?.answers)) {
      throw createApiError(
        response.data?.error || '이미지 채점 결과가 없습니다.',
        response.data?.requestId,
        null,
        { apiCode: response.data?.errorCode, model: response.data?.model }
      );
    }
    return {
      answers: response.data.answers,
      summary: response.data.summary || '',
      warnings: Array.isArray(response.data.warnings) ? response.data.warnings : [],
      answerSheetImageKey: response.data.answerSheetImageKey || '',
      answerSheetImageName: response.data.answerSheetImageName || '',
      answerSheetMimeType: response.data.answerSheetMimeType || '',
      requestId: response.data.requestId || '',
      model: response.data.model || '',
      provider: response.data.provider || '',
    };
  } catch (error) {
    if (error?.isEssayApiError) throw error;
    const responseData = error?.response?.data || {};
    throw createApiError(
      getRequestErrorMessage(error),
      responseData.requestId || '',
      error,
      { apiCode: responseData.errorCode, model: responseData.model }
    );
  }
}

export async function fetchEssayHistory() {
  try {
    const response = await axios.post(
      `${serverUrl}/aiEssay/history/list`,
      {},
      getAuthenticatedRequestConfig({ timeout: 30000 })
    );

    if (!response.data?.success || !Array.isArray(response.data?.items)) {
      throw createApiError(response.data?.error || '첨삭 내역을 불러오지 못했습니다.');
    }

    return response.data.items;
  } catch (error) {
    throw createHistoryApiError(error, '첨삭 내역을 불러오지 못했습니다.');
  }
}

export async function fetchEssayHistoryDetail(historyId) {
  if (!historyId) throw createApiError('확인할 첨삭 내역을 선택해 주세요.');

  try {
    const response = await axios.post(
      `${serverUrl}/aiEssay/history/detail`,
      { historyId },
      getAuthenticatedRequestConfig({ timeout: 30000 })
    );

    if (!response.data?.success || !response.data?.history?.result) {
      throw createApiError(response.data?.error || '첨삭 상세 내용을 불러오지 못했습니다.');
    }

    return response.data.history;
  } catch (error) {
    throw createHistoryApiError(error, '첨삭 상세 내용을 불러오지 못했습니다.');
  }
}
